import React, { useEffect, useCallback } from 'react';
import { autorun } from 'mobx';
import { useObserver } from 'mobx-react-lite';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

import { LoadingStatusType } from '@modules/core/models';
import { LoadPlaceholder } from '@modules/core/components';
import { ProductCard } from '@modules/products/components/product-card/ProductCard';
import { ProductsStoreAll } from '@modules/products/store/ProductsStoreAll';
import { Product } from '@modules/products/models';
import UserStore from '@modules/core/store/UserStore';
import { useTableScroll } from '@modules/shared/hooks';

import { EmptyProductsPage } from './EmptyProductsPage/EmptyProductsPage';
import './ProductPage.scss';

interface Props {
    productsStore?: ProductsStoreAll;
    userStore?: UserStore;
}

interface EntityRowWrapperProps {
    data: any;
    index: number;
    style: any;
}

export const ProductPage = (props: Props) => {
    const { productsStore, userStore } = props;
    const { loadProducts, products = [], loadingStatusState } = productsStore;
    const { currentUser } = userStore;
    const { setScrollNeedToUpdate, scrollbarRef } = useTableScroll(products?.length);

    const loadingFunction = useCallback(() => {
        if (currentUser?.id) {
            return loadProducts();
        }
    }, [currentUser?.id]);

    const redirectFunction = useCallback(() => {
        if (currentUser?.isInvite && products?.length === 1) {
            return window.location.replace(products[0].homeUrl);
        }
    }, [currentUser, products]);

    useEffect(() => {
        let cancelled = false;
        const request = loadingFunction();
        if (request) {
            request.catch(() => {
                cancelled = true;
            });
            return () => !cancelled && request.cancel();
        }
    }, [loadingFunction, currentUser]);

    useEffect(() => {
        const disposer = autorun(() => {
            redirectFunction();
        });
        return () => disposer();
    }, [currentUser?.id, products]);

    const EntityRowWrapper = (props: EntityRowWrapperProps) => {
        const { data, index } = props;
        return data.map((product: Product, i: number) =>
            i / 2 === index ? (
                <div key={`${i}-${index}`} className={'product-page__products-row'}>
                    <ProductCard key={`${i}-${index}-${product.code}`} product={product} />
                    {products[i + 1] ? (
                        <ProductCard key={`${i + 1}-${index}-${product.code}`} product={products[i + 1]} />
                    ) : null}
                </div>
            ) : null
        );
    };

    return useObserver(() => {
        if (loadingStatusState.status === null) {
            return null;
        }
        return (
            <main className="product-page" qa-id="product-page">
                <LoadPlaceholder
                    loadingStatus={loadingStatusState.status || LoadingStatusType.isLoaded}
                    defaultDelay={500}
                    errorIgnore
                >
                    <section className="page-body">
                        {products.length > 0 ? (
                            <AutoSizer className={'products'} onResize={() => setScrollNeedToUpdate(true)}>
                                {({ height, width }: { height: number; width: number }) => {
                                    const prop = {
                                        height,
                                        width,
                                        itemCount: Math.ceil(products.length / 2),
                                        ref: scrollbarRef,
                                        itemData: products,
                                        className: 'list'
                                    };
                                    return (
                                        <FixedSizeList {...prop} itemSize={290}>
                                            {EntityRowWrapper}
                                        </FixedSizeList>
                                    );
                                }}
                            </AutoSizer>
                        ) : (
                            <EmptyProductsPage />
                        )}
                    </section>
                </LoadPlaceholder>
            </main>
        );
    });
};
